<template>
  <UmisisModal
    ref="customStartAtModal"
    :ok-disabled="isUpdating || !customStartAtValue"
    title="Set Custom Start Date"
    @ok="submitCustomStartAt"
    @show="onModalShow"
  >
    <Field>
      <FieldLabel for="custom-start-at-datetime">
        Start date and time
      </FieldLabel>
      <Input id="custom-start-at-datetime" v-model="customStartAtValue" type="datetime-local" />
      <FieldDescription>Select the exact start date and time.</FieldDescription>
    </Field>
  </UmisisModal>
</template>

<script setup>
import { computed, ref } from 'vue'
import { toast } from 'vue-sonner'
import UmisisModal from '../../../../../../../../resources/js/components/umisis/generic/UmisisModal.vue'
import { useModalControl } from '../../../../../../../../resources/js/composables/modal/useUmisisModal.js'
import { Field, FieldDescription, FieldLabel } from '../../../../../../../../resources/js/components/ui/field/index.js'
import { Input } from '../../../../../../../../resources/js/components/ui/input/index.js'
import { useTasksStore } from '../../../stores/tasksStore.js'
import { formatTaskFieldDateTimeForToast, toTaskDateTimeLocalValue } from '../../../utils/taskFieldDateTime.js'

const props = defineProps({
  taskId: {
    type: [Number, String],
    required: true,
  },
  value: {
    type: [String, Object],
    default: null,
  },
  context: {
    type: String,
    default: 'default',
  },
})

const customStartAtValue = ref('')
const { componentRef: customStartAtModal, openModal: openDialog, closeModal: closeDialog } = useModalControl()

const tasksStore = useTasksStore(props.context)
const normalizedTaskId = computed(() => Number(props.taskId))
const isUpdating = computed(() => tasksStore.isUpdatingStartAt(normalizedTaskId.value))

const openModal = () => {
  openDialog()
}

const closeModal = () => {
  closeDialog()
}

const updateTaskStartAt = async (startAt, successMessage = 'Start date updated.') => {
  if (!Number.isInteger(normalizedTaskId.value) || normalizedTaskId.value <= 0) {
    return false
  }

  try {
    await tasksStore.updateStartAt(normalizedTaskId.value, startAt)
    toast.success(successMessage)
    return true
  } catch (error) {
    const errorMessage = error?.response?.data?.message
      || error?.response?.data?.errors?.start_at?.[0]
      || 'Unable to update the start date.'

    toast.error(errorMessage)
    return false
  }
}

const onModalShow = () => {
  customStartAtValue.value = toTaskDateTimeLocalValue(props.value)
}

const submitCustomStartAt = async (event) => {
  event?.preventDefault?.()

  if (!customStartAtValue.value) {
    return
  }

  const selectedDate = new Date(customStartAtValue.value)

  if (Number.isNaN(selectedDate.getTime())) {
    toast.error('Please provide a valid date and time.')
    return
  }

  const formattedStartAt = formatTaskFieldDateTimeForToast(selectedDate.toISOString())
  const didUpdate = await updateTaskStartAt(
    selectedDate.toISOString(),
    formattedStartAt ? `Start date set to ${formattedStartAt}.` : 'Custom start date updated.'
  )

  if (didUpdate) {
    closeDialog()
  }
}

defineExpose({
  openModal,
  closeModal,
})
</script>
